/**
 * Language Switcher Component
 * 
 * Floating button to toggle between English and Bengali.
 * Subtle by default, reveals fully on hover.
 * Styled to match the LCARS/Star Trek theme.
 */

import { motion } from 'framer-motion'
import { Globe } from 'lucide-react'
import { useTranslation, type Language } from '../i18n/index'

interface LanguageSwitcherProps {
  className?: string
}

const LANGUAGES: { code: Language; label: string; nativeLabel: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা' },
]

export function LanguageSwitcher({ className = '' }: LanguageSwitcherProps) {
  const { language, setLanguage } = useTranslation()

  const nextLang = LANGUAGES.find(l => l.code !== language) || LANGUAGES[1]

  const handleToggle = () => {
    setLanguage(nextLang.code)
  }

  return (
    <motion.button
      onClick={handleToggle}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1.5, duration: 0.5 }}
      whileHover="hover"
      whileTap={{ scale: 0.95 }}
      className={`
        fixed z-[60]
        right-4 bottom-32
        flex items-center gap-2
        px-3 py-2 rounded-full
        cursor-pointer
        transition-all duration-500 ease-out
        
        /* Default: subtle/transparent */
        bg-transparent
        border border-[rgba(0,212,255,0.15)]
        text-[rgba(0,212,255,0.4)]
        
        /* Hover: full reveal */
        hover:bg-[rgba(0,20,40,0.95)]
        hover:backdrop-blur-xl
        hover:border-[rgba(0,212,255,0.6)]
        hover:text-[#00D4FF]
        hover:shadow-[0_0_30px_rgba(0,212,255,0.4),0_0_60px_rgba(0,212,255,0.2)]
        hover:px-4
        
        ${className}
      `}
    >
      {/* Pulsing glow ring - only visible on hover */}
      <motion.span 
        className="absolute inset-0 rounded-full border border-[rgba(0,212,255,0.3)] pointer-events-none"
        initial={{ scale: 1, opacity: 0 }}
        variants={{
          hover: {
            scale: [1, 1.2, 1],
            opacity: [0, 0.5, 0],
            transition: {
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }
          }
        }}
      />
      
      <motion.div
        className="flex items-center gap-2"
        variants={{
          hover: {
            gap: '0.5rem',
          }
        }}
      >
        <Globe size={18} />
        
        {/* Text - hidden by default, shows on hover */}
        <motion.span 
          className="overflow-hidden whitespace-nowrap font-bold text-sm uppercase tracking-wider"
          initial={{ width: 0, opacity: 0 }}
          variants={{
            hover: {
              width: 'auto',
              opacity: 1,
              transition: { duration: 0.3, ease: 'easeOut' }
            }
          }}
        >
          {/* Show which language it will switch TO */}
          {language === 'en' ? 'বাংলা' : 'English'}
        </motion.span>
      </motion.div>
    </motion.button>
  )
}

/**
 * Inline Language Toggle
 * 
 * A more subtle inline toggle for use in footer or navigation.
 */
export function LanguageToggle({ className = '' }: LanguageSwitcherProps) {
  const { language, setLanguage } = useTranslation()

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {LANGUAGES.map((lang, index) => (
        <span key={lang.code} className="flex items-center gap-2">
          {index > 0 && <span className="text-[rgba(232,244,248,0.3)]">|</span>}
          <button
            onClick={() => setLanguage(lang.code)}
            className={`
              text-sm transition-colors duration-200
              ${language === lang.code 
                ? 'text-[#00D4FF] font-semibold' 
                : 'text-[rgba(232,244,248,0.5)] hover:text-[rgba(232,244,248,0.8)]'
              }
            `}
          >
            {lang.nativeLabel}
          </button>
        </span>
      ))}
    </div>
  )
}
